import config from '../config/infrastructure_outputs.json';
import { GitProvider, normalizeProvider } from '../config/providers';
/**
 * Centralized API configuration and client for the Kolega Platform
 */

export interface RequestOptions extends RequestInit {
  token?: string | null;
  impersonateUserId?: string | null;
  userId?: string | null;
}

/**
 * Error type that preserves HTTP status and response data
 */
export class ApiError extends Error {
  status: number;
  data?: any;

  constructor(message: string, status: number, data?: any) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

/**
 * Get the base API URL from environment variables or infrastructure outputs
 */
export const getApiBaseUrl = (): string => {
  return process.env.REACT_APP_API_URL || config.ApiUrl || 'http://localhost:8000';
};

/**
 * URL of the hosted MCP server that remote MCP clients add.
 * `McpUrl` is a stack output that only exists once the MCP service is deployed.
 */
export const getMcpServerUrl = (): string | null => {
  const url = process.env.REACT_APP_MCP_URL || (config as Record<string, string | undefined>).McpUrl;
  return url ? `${url.replace(/\/+$/, '')}/mcp` : null;
};

/**
 * Get the WebSocket base URL from the API base URL
 */
export const getWebSocketBaseUrl = (): string => {
  const apiUrl = new URL(getApiBaseUrl());

  // On some systems, `localhost` resolves to IPv6 (::1) first. If the dev server
  // is only bound to IPv4 (127.0.0.1), WebSocket connections to `ws://localhost`
  // can fail before hitting the backend at all.
  const hostname = apiUrl.hostname === 'localhost' ? '127.0.0.1' : apiUrl.hostname;

  const websocketProtocol = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:';
  const port = apiUrl.port ? `:${apiUrl.port}` : '';

  return `${websocketProtocol}//${hostname}${port}`;
};

const CURRENT_ORGANIZATION_STORAGE_KEY = 'kolega_current_organization_id';

export const getStoredCurrentOrganizationId = (userId?: string | null): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const urlOrgId = new URLSearchParams(window.location.search).get('org');
  if (urlOrgId) {
    return urlOrgId;
  }

  if (userId) {
    const userScopedOrgId = window.localStorage.getItem(`${CURRENT_ORGANIZATION_STORAGE_KEY}:${userId}`);
    if (userScopedOrgId) {
      return userScopedOrgId;
    }
  }

  return window.localStorage.getItem(CURRENT_ORGANIZATION_STORAGE_KEY);
};

/**
 * Base API client for making authenticated and unauthenticated requests
 */
class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = '') {
    this.baseUrl = baseUrl;
  }

  /**
   * Get the current base URL
   */
  getBaseUrl(): string {
    return this.baseUrl;
  }

  /**
   * Make a request to the API
   */
  async request<T = any>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { token, impersonateUserId, userId, ...init } = options;
    const headers = new Headers(init.headers);

    // Set default headers
    if (!headers.has('Content-Type') && !(init.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
    }

    // Add auth token if provided
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    // Attach current organization context if available
    if (typeof window !== 'undefined') {
      const orgId = getStoredCurrentOrganizationId(userId);
      if (orgId && !headers.has('X-Organization-Id')) {
        headers.set('X-Organization-Id', orgId);
      }
    }

    // Add impersonation header if provided
    if (impersonateUserId) {
      headers.set('X-Impersonate-User-ID', impersonateUserId);
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...init,
      headers,
    });

    // Handle API responses
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      const detail = data.detail;
      const message = (typeof detail === 'string' ? detail : detail?.message) || data.message || response.statusText || 'Request failed';
      throw new ApiError(message, response.status, data);
    }

    // Return JSON response if available, otherwise empty object
    return response.json().catch(() => ({})) as Promise<T>;
  }

  /**
   * Make a request that returns a Blob instead of JSON.
   */
  async requestBlob(endpoint: string, options: RequestOptions = {}): Promise<Blob> {
    const { token, impersonateUserId, userId, ...init } = options;
    const headers = new Headers(init.headers);

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    if (typeof window !== 'undefined') {
      const orgId = getStoredCurrentOrganizationId(userId);
      if (orgId && !headers.has('X-Organization-Id')) {
        headers.set('X-Organization-Id', orgId);
      }
    }

    if (impersonateUserId) {
      headers.set('X-Impersonate-User-ID', impersonateUserId);
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...init,
      headers,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      const detail = data.detail;
      const message = (typeof detail === 'string' ? detail : detail?.message) || data.message || response.statusText || 'Request failed';
      throw new ApiError(message, response.status, data);
    }

    return response.blob();
  }

  /**
   * Make a GET request
   */
  get<T = any>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  /**
   * Make a GET request for a binary/file response.
   */
  getBlob(endpoint: string, options: RequestOptions = {}): Promise<Blob> {
    return this.requestBlob(endpoint, { ...options, method: 'GET' });
  }

  /**
   * Make a POST request
   */
  post<T = any>(endpoint: string, data: any, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Make a PATCH request
   */
  patch<T = any>(endpoint: string, data: any, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * Make a PUT request
   */
  put<T = any>(endpoint: string, data: any, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * Make a DELETE request
   */
  delete<T = any>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }

  /**
   * Upload a file using FormData
   */
  async uploadFile<T = any>(endpoint: string, formData: FormData, options: RequestOptions = {}): Promise<T> {
    const { token, impersonateUserId, userId, ...init } = options;
    const headers = new Headers(init.headers);

    // Don't set Content-Type for FormData - browser will set it with boundary
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    if (typeof window !== 'undefined') {
      const orgId = getStoredCurrentOrganizationId(userId);
      if (orgId && !headers.has('X-Organization-Id')) {
        headers.set('X-Organization-Id', orgId);
      }
    }

    // Add impersonation header if provided
    if (impersonateUserId) {
      headers.set('X-Impersonate-User-ID', impersonateUserId);
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...init,
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      const uploadDetail = data.detail;
      const message = (typeof uploadDetail === 'string' ? uploadDetail : uploadDetail?.message) || data.message || response.statusText || 'Upload failed';
      throw new ApiError(message, response.status, data);
    }

    return response.json().catch(() => ({})) as Promise<T>;
  }
}

// Create and export the API client instance
export const api = new ApiClient(getApiBaseUrl());

// Export convenience functions for common API patterns
/**
 * Append a query string to `base`, skipping undefined / null / empty / false values.
 * Uses URLSearchParams encoding (spaces become "+"), so keep using `encodeURIComponent`
 * for path segments and for provider-supplied values that must round-trip byte-exact.
 */
export const withQuery = (
  base: string,
  params: Record<string, string | number | boolean | null | undefined>
): string => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === false || value === '') return;
    search.set(key, value === true ? 'true' : String(value));
  });
  const queryString = search.toString();
  return queryString ? `${base}?${queryString}` : base;
};

export const apiEndpoints = {
  health: '/health',
  workspaces: '/workspaces',
  workspace: (id: string) => `/workspaces/${id}`,
  workspaceRunningJobs: (id: string) => `/workspaces/${id}/running-jobs`,
  
  // Workspace files endpoints (code files)
  workspaceFiles: (workspaceId: string) => `/workspaces/${workspaceId}/files`,
  workspaceFileContent: (workspaceId: string) => `/workspaces/${workspaceId}/file-content`,
  workspaceSaveFile: (workspaceId: string) => `/workspaces/${workspaceId}/save-file`,
  workspaceDownloadRepository: (workspaceId: string) => `/workspaces/${workspaceId}/download-repository`,
  
  // Template endpoints
  templates: '/templates',
  template: (id: string) => `/templates/${id}`,
  
  // GitHub integration (user-level)
  githubStatus: '/github/status',
  githubConnect: '/github/connect',
  githubRepos: '/github/repos',

  // GitHub integration (organization-level)
  orgGithub: {
    status: (orgId: string) => `/organizations/${orgId}/github/status`,
    connect: (orgId: string) => `/organizations/${orgId}/github/connect`,
    disconnect: (orgId: string) => `/organizations/${orgId}/github/disconnect`,
    repos: (orgId: string) => `/organizations/${orgId}/github/repos`,
    repoBranches: (orgId: string, repoFullName: string) =>
      `/organizations/${orgId}/github/repos/${repoFullName}/branches`,
  },
  
  // GitLab integration (organization-level)
  orgGitlab: {
    status: (orgId: string) => `/organizations/${orgId}/gitlab/status`,
    connect: (orgId: string) => `/organizations/${orgId}/gitlab/connect`,
    connectSelfHostedOAuth: (orgId: string) => `/organizations/${orgId}/gitlab/connect-self-hosted-oauth`,
    disconnect: (orgId: string, instanceUrl?: string) =>
      instanceUrl
        ? `/organizations/${orgId}/gitlab/disconnect?instance_url=${encodeURIComponent(instanceUrl)}`
        : `/organizations/${orgId}/gitlab/disconnect`,
    repos: (
      orgId: string,
      instanceUrlOrOptions?:
        | string
        | {
            instanceUrl?: string;
            discoveryMode?: 'membership' | 'discover';
            namespace?: string;
          }
    ) => {
      const options =
        typeof instanceUrlOrOptions === 'string'
          ? { instanceUrl: instanceUrlOrOptions }
          : instanceUrlOrOptions || {};

      return withQuery(`/organizations/${orgId}/gitlab/repos`, {
        instance_url: options.instanceUrl,
        discovery_mode:
          options.discoveryMode && options.discoveryMode !== 'membership' ? options.discoveryMode : undefined,
        namespace: options.namespace,
      });
    },
    repoBranches: (orgId: string, repoFullName: string, instanceUrl?: string) =>
      instanceUrl
        ? `/organizations/${orgId}/gitlab/repos/${encodeURIComponent(repoFullName)}/branches?instance_url=${encodeURIComponent(instanceUrl)}`
        : `/organizations/${orgId}/gitlab/repos/${encodeURIComponent(repoFullName)}/branches`,
  },

  // Azure DevOps integration (organization-level)
  orgAzureDevOps: {
    status: (orgId: string, validate?: boolean) =>
      `/organizations/${orgId}/azure-devops/status${validate ? '?validate=true' : ''}`,
    connect: (orgId: string) => `/organizations/${orgId}/azure-devops/connect`,
    disconnect: (orgId: string) => `/organizations/${orgId}/azure-devops/disconnect`,
    projects: (orgId: string) => `/organizations/${orgId}/azure-devops/projects`,
    repos: (orgId: string, project: string) =>
      `/organizations/${orgId}/azure-devops/repos?project=${encodeURIComponent(project)}`,
    repoBranches: (orgId: string, repoFullName: string) =>
      `/organizations/${orgId}/azure-devops/repos/${encodeURIComponent(repoFullName)}/branches`,
  },

  // AWS CodeCommit integration (organization-level; one connection per account+region)
  orgCodeCommit: {
    status: (orgId: string, validate?: boolean) =>
      withQuery(`/organizations/${orgId}/codecommit/status`, { validate }),
    connect: (orgId: string) => `/organizations/${orgId}/codecommit/connect`,
    disconnect: (orgId: string, connectionId?: string) =>
      withQuery(`/organizations/${orgId}/codecommit/disconnect`, { connection_id: connectionId }),
    roleSetup: (orgId: string) => `/organizations/${orgId}/codecommit/role-setup`,
    repos: (orgId: string, options?: { connectionId?: string }) =>
      withQuery(`/organizations/${orgId}/codecommit/repos`, { connection_id: options?.connectionId }),
    repoBranches: (orgId: string, repoFullName: string) =>
      `/organizations/${orgId}/codecommit/repos/${encodeURIComponent(repoFullName)}/branches`,
  },

  // Admin endpoints
  admin: {
    impersonateCheck: '/admin/impersonate/check',
    listAdmins: '/admin/users/admins',
    grantAdmin: '/admin/users/admins/grant',
    revokeAdmin: '/admin/users/admins/revoke',
    subscriptionTiers: '/admin/subscription-tiers',
    subscriptionTier: (id: string) => `/admin/subscription-tiers/${id}`,
    subscriptionTierUsage: (id: string) => `/admin/subscription-tiers/${id}/usage`,
    subscriptionTierSetDefault: (id: string) => `/admin/subscription-tiers/${id}/set-default`,
    modelPricing: '/admin/model-pricing',
    modelPricingById: (id: string) => `/admin/model-pricing/${id}`,
    modelPricingProviders: '/admin/model-pricing/providers',
    modelPricingModelsByProvider: (provider: string) => `/admin/model-pricing/models/${provider}`,
    debugWorkspacePrompt: '/admin/debug/workspace-prompt',
    usageCostsByScan: '/admin/usage-costs/by-scan',
    usageCostsByUser: '/admin/usage-costs/by-user',
    usageCostsByAssessment: '/admin/usage-costs/by-assessment',
    organizationCosts: '/admin/organization-costs',
    organizationCostsScans: (orgId: string) => `/admin/organization-costs/${orgId}/scans`,
    organizationCostsBillingPeriods: '/admin/organization-costs/billing-periods',
    resetUserPassword: '/admin/reset-user-password',
  },
  
  // Billing endpoints
  billing: {
    paymentConfig: '/billing/payment-config',
    paymentMethods: '/billing/payment-methods',
    quotaBalance: '/billing/quotas/balance',
    quotaTopup: '/billing/quotas/topup',
    bundleTopup: '/billing/quotas/bundle-topup',
    billingInfo: '/billing/billing-info',
    invoices: '/billing/invoices',
    subscription: '/billing/subscription',
    subscriptionTiers: '/billing/subscription/tiers',
    subscriptionUncancel: '/billing/subscription/uncancel',
    hasSubscriptionAccess: '/billing/has-subscription-access',
  },
  // Integration endpoints
  integrations: {
  },
  // Support request endpoints
  supportRequests: {
    create: '/support-requests',
    admin: {
      list: '/admin/support-requests',
      get: (ticketNumber: string) => `/admin/support-requests/${ticketNumber}`,
      updateStatus: (ticketNumber: string) => `/admin/support-requests/${ticketNumber}/status`,
    },
  },
  // Feature flags/endpoints
  features: {
    effective: '/features/effective',
    admin: {
      listFlags: '/admin/features/flags',
      upsertFlag: (key: string) => `/admin/features/flags/${key}`,
      deleteFlag: (key: string) => `/admin/features/flags/${key}`,
      getUserOverrides: (userId: string) => `/admin/features/users/${userId}/overrides`,
      upsertUserOverrides: (userId: string) => `/admin/features/users/${userId}/overrides`,
      deleteUserOverrides: (userId: string) => `/admin/features/users/${userId}/overrides`,
      listUserOverrides: (limit = 100, offset = 0) => `/admin/features/users/overrides?limit=${limit}&offset=${offset}`,
    },
  },
  // Organization API key management (Clerk-authenticated admin endpoints)
  orgApiKeys: {
    list: (orgId: string, includeRevoked = false) =>
      `/organizations/${orgId}/api-keys${includeRevoked ? '?include_revoked=true' : ''}`,
    create: (orgId: string) => `/organizations/${orgId}/api-keys`,
    revoke: (orgId: string, keyId: string) => `/organizations/${orgId}/api-keys/${keyId}`,
  },
  // Device authorization grant — CLI pairing approval flow
  deviceAuth: {
    get: (userCode: string) => `/device-authorizations/${encodeURIComponent(userCode)}`,
    approve: (userCode: string) => `/device-authorizations/${encodeURIComponent(userCode)}/approve`,
    deny: (userCode: string) => `/device-authorizations/${encodeURIComponent(userCode)}/deny`,
  },
  // OAuth consent for remote MCP clients
  oauthConsent: {
    get: (requestId: string) => `/oauth-consent/${encodeURIComponent(requestId)}`,
    approve: (requestId: string) => `/oauth-consent/${encodeURIComponent(requestId)}/approve`,
    deny: (requestId: string) => `/oauth-consent/${encodeURIComponent(requestId)}/deny`,
  },
  // Connected apps — OAuth grants for an organization
  orgOAuthGrants: {
    list: (orgId: string) => `/organizations/${orgId}/oauth-grants`,
    revoke: (orgId: string, grantId: string) => `/organizations/${orgId}/oauth-grants/${grantId}`,
  },
  onboarding: {
    demoRepositories: '/onboarding/demo-repositories',
    demoScan: '/onboarding/demo-scan',
  },
  // Application endpoints (organization-scoped)
  applications: {
    list: (orgId: string, includeArchived = false) =>
      `/organizations/${orgId}/applications${includeArchived ? '?include_archived=true' : ''}`,
    create: (orgId: string) => `/organizations/${orgId}/applications`,
    createPublicGithub: (orgId: string) => `/organizations/${orgId}/applications/public-github`,
    get: (orgId: string, appId: string) => `/organizations/${orgId}/applications/${appId}`,
    update: (orgId: string, appId: string) => `/organizations/${orgId}/applications/${appId}`,
    archive: (orgId: string, appId: string) => `/organizations/${orgId}/applications/${appId}/archive`,
    unarchive: (orgId: string, appId: string) => `/organizations/${orgId}/applications/${appId}/unarchive`,
    updateProfile: (orgId: string, appId: string) => `/organizations/${orgId}/applications/${appId}/profile`,
    updateRepoBranch: (orgId: string, appId: string, repoFullName: string) =>
      `/organizations/${orgId}/applications/${appId}/repositories/${repoFullName}/branch`,
    scanSchedules: (orgId: string, appId: string) =>
      `/organizations/${orgId}/applications/${appId}/scan-schedules`,
    scanSchedule: (orgId: string, appId: string, scanType: string) =>
      `/organizations/${orgId}/applications/${appId}/scan-schedules/${scanType}`,
    publicScanPage: (orgId: string, appId: string) =>
      `/organizations/${orgId}/applications/${appId}/public-scan-page`,
    publicScanPagePreview: (orgId: string, appId: string) =>
      `/organizations/${orgId}/applications/${appId}/public-scan-page/preview`,
    publicScanPageRefresh: (orgId: string, appId: string) =>
      `/organizations/${orgId}/applications/${appId}/public-scan-page/refresh`,
  },
  publicScanProjects: {
    get: (orgSlug: string, projectSlug: string) =>
      `/public/projects/${encodeURIComponent(orgSlug)}/${encodeURIComponent(projectSlug)}`,
    badge: (orgSlug: string, projectSlug: string) =>
      `/public/projects/${encodeURIComponent(orgSlug)}/${encodeURIComponent(projectSlug)}/badge.svg`,
  },
  // Repository sandbox config endpoints (uses owner/repo path format)
  repoSandboxConfig: {
    get: (orgId: string, appId: string, repoFullName: string) =>
      `/organizations/${orgId}/applications/${appId}/repositories/${repoFullName}/sandbox-config`,
    updateSetupScript: (orgId: string, appId: string, repoFullName: string) =>
      `/organizations/${orgId}/applications/${appId}/repositories/${repoFullName}/sandbox-config/setup-script`,
    envVars: (orgId: string, appId: string, repoFullName: string) =>
      `/organizations/${orgId}/applications/${appId}/repositories/${repoFullName}/sandbox-config/environment-variables`,
    envVar: (orgId: string, appId: string, repoFullName: string, envVarId: string) =>
      `/organizations/${orgId}/applications/${appId}/repositories/${repoFullName}/sandbox-config/environment-variables/${envVarId}`,
    updateNetworkAccess: (orgId: string, appId: string, repoFullName: string) =>
      `/organizations/${orgId}/applications/${appId}/repositories/${repoFullName}/sandbox-config/network-access`,
  },
  // Fix endpoints (organization-scoped)
  fixes: {
    list: (orgId: string) => `/organizations/${orgId}/fixes`,
    board: (orgId: string) => `/organizations/${orgId}/fixes/board`,
    listByFinding: (orgId: string, findingId: string) =>
      `/organizations/${orgId}/fixes?finding_id=${findingId}`,
    create: (orgId: string) => `/organizations/${orgId}/fixes`,
    get: (orgId: string, fixId: string) => `/organizations/${orgId}/fixes/${fixId}`,
    diff: (orgId: string, fixId: string) => `/organizations/${orgId}/fixes/${fixId}/diff`,
    createPR: (orgId: string, fixId: string) => `/organizations/${orgId}/fixes/${fixId}/create-pr`,
    regenerate: (orgId: string, fixId: string) => `/organizations/${orgId}/fixes/${fixId}/regenerate`,
    refine: (orgId: string, fixId: string) => `/organizations/${orgId}/fixes/${fixId}/refine`,
    cancel: (orgId: string, fixId: string) => `/organizations/${orgId}/fixes/${fixId}/cancel`,
    delete: (orgId: string, fixId: string) => `/organizations/${orgId}/fixes/${fixId}`,
    archive: (orgId: string, fixId: string) => `/organizations/${orgId}/fixes/${fixId}/archive`,
    retry: (orgId: string, fixId: string) => `/organizations/${orgId}/fixes/${fixId}/retry`,
    messages: (orgId: string, fixId: string) => `/organizations/${orgId}/fixes/${fixId}/messages`,
  },
  // Scan endpoints
  scans: {
    create: () => `/scans`,
    list: () => `/scans`,
    branches: (applicationId?: string) => {
      const params = new URLSearchParams();
      if (applicationId) params.set('application_id', applicationId);
      const queryString = params.toString();
      return `/scans/branches${queryString ? `?${queryString}` : ''}`;
    },
    listBatches: (applicationId?: string) => {
      const params = new URLSearchParams();
      if (applicationId) params.set('application_id', applicationId);
      params.set('limit', '100'); // Get recent batches
      const queryString = params.toString();
      return `/scans${queryString ? `?${queryString}` : ''}`;
    },
    get: (scanId: string) => `/scans/${scanId}`,
    getResults: (scanId: string) => `/scans/${scanId}/results`,
    getBatch: (batchId: string) => `/scans/batch/${batchId}`,
    getBatchProgress: (batchId: string) => `/scans/batch/${batchId}/progress`,
  },
  // Finding endpoints
  findings: {
    list: () => `/findings`,
    get: (findingId: string) => `/findings/${findingId}`,
    updateStatus: (findingId: string) => `/findings/${findingId}/status`,
    occurrences: (findingId: string) => `/findings/${findingId}/occurrences`,
    evidence: (findingId: string) => `/findings/${findingId}/evidence`,
    byScan: (scanId: string) => `/findings/scan/${scanId}`,
    byApplication: (applicationId: string) => `/findings/application/${applicationId}`,
    applicationStats: (applicationId: string) => `/findings/application/${applicationId}/stats`,
    autofix: (findingId: string) => `/findings/${findingId}/autofix`,
    assess: (findingId: string) => `/findings/${findingId}/assess`,
    assessment: (findingId: string) => `/findings/${findingId}/assessment`,
    batchAssess: (scanId: string) => `/findings/scan/${scanId}/assess`,
    assessmentStatus: (scanId: string) => `/findings/scan/${scanId}/assessment-status`,
    activeAssessments: () => `/findings/active-assessments`,
    trends: (days?: number, applicationIds?: string) => {
      const params = new URLSearchParams();
      if (days) params.set('days', days.toString());
      if (applicationIds) params.set('application_ids', applicationIds);
      const queryString = params.toString();
      return `/findings/trends${queryString ? `?${queryString}` : ''}`;
    },
  },
  // SBOM endpoints
  sboms: {
    listForApplication: (applicationId: string, limit?: number, skip?: number) => {
      const params = new URLSearchParams();
      if (limit) params.set('limit', limit.toString());
      if (skip) params.set('skip', skip.toString());
      const qs = params.toString();
      return `/sboms/applications/${applicationId}${qs ? `?${qs}` : ''}`;
    },
    get: (sbomId: string) => `/sboms/${sbomId}`,
    components: (
      sbomId: string,
      filters?: {
        ecosystem?: string;
        hasCve?: boolean;
        licenseId?: string;
        nameContains?: string;
        limit?: number;
        skip?: number;
      },
    ) => {
      const params = new URLSearchParams();
      if (filters?.ecosystem) params.set('ecosystem', filters.ecosystem);
      if (filters?.hasCve !== undefined) params.set('has_cve', String(filters.hasCve));
      if (filters?.licenseId) params.set('license_id', filters.licenseId);
      if (filters?.nameContains) params.set('name_contains', filters.nameContains);
      if (filters?.limit) params.set('limit', filters.limit.toString());
      if (filters?.skip) params.set('skip', filters.skip.toString());
      const qs = params.toString();
      return `/sboms/${sbomId}/components${qs ? `?${qs}` : ''}`;
    },
    download: (sbomId: string) => `/sboms/${sbomId}/download`,
    diff: (sbomId: string, otherSbomId: string) => `/sboms/${sbomId}/diff/${otherSbomId}`,
  },
  licensePolicy: {
    get: () => `/license-policy`,
    update: () => `/license-policy`,
  },
  sla: {
    config: (orgId: string) => `/organizations/${orgId}/sla-config`,
    report: (orgId: string) => `/organizations/${orgId}/sla-report`,
    calendar: (orgId: string) => `/organizations/${orgId}/business-calendar`,
    findingEvents: (orgId: string) => `/organizations/${orgId}/finding-events`,
  },
  compliance: {
    refresh: (orgId: string) => `/organizations/${orgId}/compliance/refresh`,
    overview: (orgId: string, days?: number, applicationIds?: string) => {
      const params = new URLSearchParams();
      if (days) params.set('days', days.toString());
      if (applicationIds) params.set('application_ids', applicationIds);
      const queryString = params.toString();
      return `/organizations/${orgId}/compliance/overview${queryString ? `?${queryString}` : ''}`;
    },
    requirements: (orgId: string, days?: number, applicationIds?: string) => {
      const params = new URLSearchParams();
      if (days) params.set('days', days.toString());
      if (applicationIds) params.set('application_ids', applicationIds);
      const queryString = params.toString();
      return `/organizations/${orgId}/compliance/requirements${queryString ? `?${queryString}` : ''}`;
    },
    thresholdDetails: (
      orgId: string,
      scanType: string,
      severity: string,
      options?: {
        days?: number;
        status?: string;
        applicationIds?: string;
        search?: string;
        sortBy?: string;
        sortOrder?: string;
        limit?: number;
        skip?: number;
      }
    ) => {
      const params = new URLSearchParams();
      params.set('scan_type', scanType);
      params.set('severity', severity);
      if (options?.days) params.set('days', options.days.toString());
      if (options?.status) params.set('status', options.status);
      if (options?.applicationIds) params.set('application_ids', options.applicationIds);
      if (options?.search) params.set('search', options.search);
      if (options?.sortBy) params.set('sort_by', options.sortBy);
      if (options?.sortOrder) params.set('sort_order', options.sortOrder);
      if (options?.limit) params.set('limit', options.limit.toString());
      if (options?.skip) params.set('skip', options.skip.toString());
      return `/organizations/${orgId}/compliance/threshold-details?${params.toString()}`;
    },
    workload: (orgId: string, applicationId?: string, limit?: number, skip?: number, actionableOnly?: boolean) => {
      const params = new URLSearchParams();
      if (applicationId) params.set('application_id', applicationId);
      if (limit) params.set('limit', limit.toString());
      if (skip) params.set('skip', skip.toString());
      if (actionableOnly !== undefined) params.set('actionable_only', actionableOnly.toString());
      const queryString = params.toString();
      return `/organizations/${orgId}/compliance/workload${queryString ? `?${queryString}` : ''}`;
    },
  },
};

/**
 * Branch-listing endpoint for a repository, dispatched on provider. Missing/unknown
 * providers resolve to GitHub (legacy repositories predate the `provider` field).
 */
export const repoBranchesEndpoint = (
  orgId: string,
  provider: GitProvider | string | null | undefined,
  repoFullName: string,
  gitlabInstanceUrl?: string
): string => {
  switch (normalizeProvider(provider)) {
    case 'github':
      return apiEndpoints.orgGithub.repoBranches(orgId, repoFullName);
    case 'azure_devops':
      return apiEndpoints.orgAzureDevOps.repoBranches(orgId, repoFullName);
    case 'codecommit':
      return apiEndpoints.orgCodeCommit.repoBranches(orgId, repoFullName);
    case 'gitlab':
    default:
      return apiEndpoints.orgGitlab.repoBranches(orgId, repoFullName, gitlabInstanceUrl);
  }
};

// Explicitly export the inferred type to stabilize downstream type-checking (e.g. CRA/ForkTsChecker).
export type ApiEndpoints = typeof apiEndpoints;
